import { useMemo } from "react"
import { ColumnDef } from "@tanstack/react-table"
import DataTable from "Components/molecule/data-table"
import useDataTable from "hooks/useDataTable"
import { OpportunityListRow } from "models/opportunity-dashboard"
import { money, monthLabel } from "./format"

interface OpportunityListTableProps {
    rows: OpportunityListRow[]
}

const footerClass = () => "financial-cell-footer"
const textCell = ({ getValue }: any) => getValue() ?? "—"

// Description, Stage, Owner, Client, Close Month, Result Month and Value, with the total value.
const OpportunityListTable = ({ rows }: OpportunityListTableProps) => {
    const total = useMemo(() => rows.reduce((sum, r) => sum + r.value, 0), [rows])

    const columns = useMemo<ColumnDef<OpportunityListRow>[]>(() => [
        {
            accessorKey: "description",
            header: "Description",
            size: 260,
            meta: { getCellTitle: (row: any) => row.original.description, getFooterClassName: footerClass },
            footer: () => `Total (${rows.length})`,
        },
        { accessorKey: "stage", header: "Stage", size: 140, meta: { getFooterClassName: footerClass } },
        { accessorKey: "owner", header: "Owner", size: 140, cell: textCell, meta: { getFooterClassName: footerClass } },
        {
            accessorKey: "client",
            header: "Client",
            size: 190,
            cell: textCell,
            meta: { getCellTitle: (row: any) => row.original.client ?? undefined, getFooterClassName: footerClass },
        },
        {
            id: "closeMonth",
            accessorFn: r => r.closeDate ?? undefined,
            header: "Closed Month",
            size: 105,
            cell: ({ row }) => monthLabel(row.original.closeDate),
            meta: { getFooterClassName: footerClass },
        },
        {
            id: "resultMonth",
            accessorFn: r => r.resultDate ?? undefined,
            header: "Result Month",
            size: 105,
            cell: ({ row }) => monthLabel(row.original.resultDate),
            meta: { getFooterClassName: footerClass },
        },
        {
            accessorKey: "value",
            header: "Value",
            size: 130,
            cell: ({ getValue }) => money(getValue<number>()),
            footer: () => money(total),
            meta: {
                getHeaderClassName: () => "text-end",
                cellClassName: "text-end",
                getFooterClassName: () => "financial-cell-footer text-end",
            },
        },
    ], [rows.length, total])

    const table = useDataTable({ data: rows, columns })

    return <DataTable table={table} columns={columns} />
}

export default OpportunityListTable
